/**
 * Animated videos: the quick-tool video type whose pictures are drawn in code
 * by the motion engine (/motion) instead of by an image or video model.
 *
 * Shared by the server (pricing, limits, the render queue) and the web page.
 */

/** `ai_music_videos.videoType` for these rows. */
export const ANIMATED_VIDEO_TYPE = "animated" as const;

/** Credits per second of video (Kitty, Oct 13 2026). */
export const ANIMATED_VIDEO_CREDITS_PER_SECOND = 3;

/**
 * Longest video the pack can carry: its 18 scenes are stretched over the
 * song, and past five minutes each one sits on screen too long.
 */
export const ANIMATED_VIDEO_MAX_SECONDS = 5 * 60;

/** Below this there is no room for a title, one scene and an ending. */
export const ANIMATED_VIDEO_MIN_SECONDS = 12;

export function animatedVideoCreditCost(durationSeconds: number): number {
  return Math.ceil(Math.max(0, durationSeconds) * ANIMATED_VIDEO_CREDITS_PER_SECOND);
}

/** Who may create animated videos: nobody, admins only (a production trial), or everyone. */
export type AnimatedVideoAccess = "off" | "admin" | "on";

export function parseAnimatedVideoAccess(value: string | undefined | null): AnimatedVideoAccess {
  const v = (value ?? "").trim().toLowerCase();
  if (v === "true" || v === "on" || v === "1") return "on";
  if (v === "admin") return "admin";
  return "off";
}

export function animatedVideoAllowedFor(access: AnimatedVideoAccess, isAdmin: boolean): boolean {
  return access === "on" || (access === "admin" && isAdmin);
}

/** A render's place in its life, as the page shows it. */
export type AnimatedVideoStage = "queued" | "planning" | "rendering" | "uploading" | "done" | "failed";

/** Line and title lengths the title card can hold. */
export const ANIMATED_VIDEO_TITLE_MAX = 80;
export const ANIMATED_VIDEO_CREDIT_MAX = 60;
